import type { WcCompleteFrame, WcDecoderBackend, WcDecoderPlatform, WcVideoFrame } from '../client/WebCodecsClientTypes';

export interface WcDecoderEvents {
  output(frame: WcVideoFrame, frameId: number, elapsedUs: number): void;
  failed(reason: string): void;
  needsKeyframe?(reason: 'decode-error'): void;
  unsupported?(codec: string, message: string): void;
}
/**
 * Counts submitted-but-not-output frames even when decodeQueueSize is already zero.
 * VideoToolbox (Safari) will not emit until several frames are queued, and it keeps that
 * pipeline full. That hold is not receiver backlog: only frames beyond it are.
 */
export class WebCodecsDecoder {
  private static readonly primeCap = 8;
  private backend: WcDecoderBackend | null = null;
  private config: VideoDecoderConfig | null = null;
  private generation = 0;
  private needsKey = true;
  private visible = true;
  private closed = false;
  private nextTimestamp = 0;
  private warmedUp = false;
  /** Frames still inside the decoder after the latest output: its steady pipeline, not a stall. */
  private hold = 0;
  private lastOutputUs = 0;
  private readonly pending = new Map<number, { id: number; submittedUs: number }>();
  constructor(private readonly platform: WcDecoderPlatform, private readonly nowUs: () => number,
    private readonly events: WcDecoderEvents) {}
  configure(config: VideoDecoderConfig): boolean {
    // Enforce the gameplay request at the final boundary, including every reset.
    // Browser selection remains a preference; this is not hardware attestation.
    this.config = { ...config, hardwareAcceleration: 'prefer-hardware', optimizeForLatency: true };
    return this.reset();
  }
  reset(): boolean {
    ++this.generation; this.backend?.close(); this.backend = null; this.pending.clear();
    this.needsKey = true; this.warmedUp = false; this.hold = 0; this.lastOutputUs = 0;
    if (!this.config || this.closed) return false;
    const generation = this.generation;
    try {
      this.backend = this.platform.createDecoder(frame => this.output(generation, frame), error => {
        if (generation !== this.generation || this.closed) return;
        if (isNotSupported(error)) { this.unsupported(error.message); return; }
        this.events.needsKeyframe?.('decode-error'); this.reset();
      });
      this.backend.configure(this.config);
      return true;
    } catch (error) {
      if (isNotSupported(error)) { this.unsupported(error instanceof Error ? error.message : 'NotSupportedError'); return false; }
      this.fail('Decoder configuration failed'); return false;
    }
  }
  canAccept(): boolean {
    return !!this.backend && !this.closed && this.visible &&
      this.pending.size < this.maxPending() && this.backend.decodeQueueSize < this.maxPending();
  }
  submit(frame: WcCompleteFrame): boolean {
    const backend = this.backend;
    if (!backend || !this.canAccept() || (this.needsKey && !frame.key)) return false;
    // Local opaque correlation timestamp: host capture/enqueue clocks are never subtracted from client clocks.
    const timestamp = ++this.nextTimestamp;
    this.pending.set(timestamp, { id: frame.frameId, submittedUs: this.nowUs() });
    try {
      backend.decode({ type: frame.key ? 'key' : 'delta', timestamp, duration: frame.durationUs, data: frame.bytes });
      this.needsKey = false; return true;
    } catch { this.pending.delete(timestamp); this.events.needsKeyframe?.('decode-error'); this.reset(); return false; }
  }
  setVisible(visible: boolean): void { this.visible = visible; if (!visible) this.reset(); }
  /** Frames beyond the decoder's own pipeline, capped at the feedback wire's limit of 2. */
  getInFlight(): number { return Math.min(2, Math.max(0, this.pending.size - this.hold)); }
  getQueueSize(): number { return Math.min(2, Math.max(0, (this.backend?.decodeQueueSize ?? 0) - this.hold)); }
  oldestAgeUs(): number { return Math.max(0, ...[...this.pending.values()].map(frame => this.nowUs() - frame.submittedUs)); }
  // Hardware decoder initialization routinely delays the first output past the steady-state deadline.
  // After that, silence since the last output is a stall. A pipeline that is still emitting is not.
  tick(): void {
    const deadlineUs = this.warmedUp ? 100000 : 1000000;
    const quietUs = this.lastOutputUs ? this.nowUs() - this.lastOutputUs : this.oldestAgeUs();
    if (this.pending.size && quietUs > deadlineUs) { this.events.needsKeyframe?.('decode-error'); this.reset(); }
  }
  close(): void {
    this.closed = true; ++this.generation; this.backend?.close(); this.backend = null; this.pending.clear();
  }
  private output(generation: number, frame: WcVideoFrame): void {
    if (generation !== this.generation || this.closed || !this.visible) { frame.close(); return; }
    const entry = this.pending.get(frame.timestamp);
    if (!entry) { frame.close(); return; }
    // VideoToolbox (Safari) may decode a frame without outputting it; anything submitted earlier will never come.
    for (const timestamp of this.pending.keys()) { if (timestamp === frame.timestamp) break; this.pending.delete(timestamp); }
    this.pending.delete(frame.timestamp); this.warmedUp = true; this.lastOutputUs = this.nowUs();
    this.hold = this.pending.size >= this.hold ? this.pending.size : Math.max(0, this.hold - 1);
    if ((frame.displayWidth ?? frame.codedWidth) !== this.config?.codedWidth || (frame.displayHeight ?? frame.codedHeight) !== this.config?.codedHeight) {
      frame.close(); this.fail('Decoded dimensions changed without configuration'); return;
    }
    this.events.output(frame, entry.id, Math.max(0, this.nowUs() - entry.submittedUs));
  }
  private maxPending(): number {
    return this.warmedUp ? Math.min(WebCodecsDecoder.primeCap, Math.max(2, this.hold + 2)) : WebCodecsDecoder.primeCap;
  }
  private unsupported(message: string): void {
    if (this.closed) return;
    this.closed = true; this.backend?.close(); this.backend = null; this.pending.clear();
    this.events.unsupported?.(this.config?.codec ?? '', message);
  }
  private fail(reason: string): void { if (!this.closed) { this.close(); this.events.failed(reason); } }
}

function isNotSupported(error: unknown): boolean {
  return !!error && typeof error === 'object' && 'name' in error && (error as { name: unknown }).name === 'NotSupportedError';
}
